import { Rarity, Tag, type ModDef } from '../../sim/mods';
import { Stat } from '../../sim/stats';

/* ═══════════════════════════════════════════════════════════════════════════
   Mods d'ARME SECONDAIRE — dixième famille du pool.

   Elles affûtent le clic droit : recharge plus courte, dégâts plus lourds.
   `dps()` ne regarde que la primaire, donc les statistiques de secondaire ne
   déplacent JAMAIS la courbe de puissance mesurée par le banc
   (tests/balance/curve.test.ts) — c'est leur intérêt et leur garde-fou.

   ── Pourquoi chacune porte AUSSI un petit rendement PRIMAIRE ────────────────
   Ce n'est pas de la générosité, c'est de l'arithmétique de pool, et le banc
   d'équilibrage l'impose. Le nombre de cartes prises dans une run est FIXE
   (vingt). Ajouter trois cartes qui ne rendent rien à la primaire, c'est
   remplacer trois cartes de dégâts par du vide dans une pioche sur sept :
   mesuré, la médiane du niveau 20 chutait de 3 160 à 2 806 (−9,5 %) et la
   dispersion frôlait son plafond. Un petit rendement primaire — cadence ou
   dégâts — rend leur contribution comparable à celle d'une carte ordinaire, et
   la courbe redevient celle d'avant. Le même raccommodage que les cartes
   utilitaires du pool, qui portent toutes un filet de dégâts pour la même raison
   (voir `lest-largue`, `dissipateur`). La `Batterie auxiliaire`, elle, reste
   purement secondaire : une seule de la famille suffit à diluer sans peser.

   ── Pourquoi la recharge s'écrit en NÉGATIF ────────────────────────────────
   `secondaryCooldownMul` est le seul multiplicateur du jeu où PLUS BAS = MIEUX
   (1 = inchangé, 0,8 = recharge en 80 % du temps). Une carte de recharge est
   donc un `mul` négatif — « −20 % de recharge » vaut `value: -0,2`. Le texte dit
   le chiffre exact, jamais « recharge plus rapide » : le joueur compare deux
   cartes au chiffre près (§6.7). Ce `value` négatif n'entre pas dans le contrôle
   « au moins un effet positif » de pool.test.ts — d'où l'importance que chaque
   carte porte, à côté, un gain à `value` positif.

   ── Les bornes ─────────────────────────────────────────────────────────────
   Empiler ces cartes (et le FANAL) ne dégénère pas : `STAT_FLOORS` plafonne la
   recharge à ×0,4 (au plus 2,5 fois plus rapide) et `STAT_CAPS` les dégâts
   secondaires à ×2,5. Au-delà, la secondaire redeviendrait une primaire bis, ce
   que `validateSecondaries` refuse à une définition et que ces bornes refusent
   à un build.

   ── Les tags ───────────────────────────────────────────────────────────────
   Trois cartes, six tags répartis un par un : la famille n'aspire aucune voie
   et ne déséquilibre pas le compteur de synergies. Chaque carte alimente deux
   fusions différentes en plus d'affûter la secondaire.
   ═══════════════════════════════════════════════════════════════════════════ */

const mods: ModDef[] = [
  {
    id: 'autochargeur',
    name: 'Autochargeur',
    rarity: Rarity.Common,
    tags: [Tag.Thermique, Tag.Drone],
    maxStacks: 2,
    // Faire tourner l'armement plus vite, primaire ET secondaire : la recharge
    // du clic droit fond, la cadence du tir automatique gagne un cran. Commune,
    // donc fréquente — c'est la carte par laquelle un joueur découvre que sa
    // secondaire peut se jouer bien plus souvent.
    modifiers: [
      { stat: Stat.SecondaryCooldownMul, op: 'mul', value: -0.2 },
      { stat: Stat.FireRate, op: 'mul', value: 0.08 },
    ],
    text: '−20 % de recharge des secondaires, +8 % de cadence',
  },
  {
    id: 'ogives-amplifiees',
    name: 'Ogives amplifiées',
    rarity: Rarity.Rare,
    tags: [Tag.Plasma, Tag.Surcharge],
    maxStacks: 2,
    // Le versant PUISSANCE : des charges plus lourdes des deux côtés. L'essentiel
    // va à la secondaire (+30 %), un filet à la primaire (+6 %) — assez pour
    // qu'elle ne dilue pas la courbe, pas assez pour en faire une carte de dégâts
    // déguisée.
    modifiers: [
      { stat: Stat.SecondaryDamageMul, op: 'mul', value: 0.3 },
      { stat: Stat.Damage, op: 'mul', value: 0.06 },
    ],
    text: '+30 % de dégâts des secondaires, +6 % de dégâts',
  },
  {
    id: 'batterie-auxiliaire',
    name: 'Batterie auxiliaire',
    rarity: Rarity.Rare,
    tags: [Tag.Cinetique, Tag.Orbital],
    maxStacks: 2,
    // La seule purement secondaire, et c'est voulu : elle cumule recharge et
    // dégâts du clic droit, sans une once de primaire. Une carte qui ne dilue
    // que d'un cran — le banc tolère une, pas trois. Deux tags encore inédits
    // dans la famille, pour qu'elle nourrisse quatre fusions à elle seule.
    modifiers: [
      { stat: Stat.SecondaryCooldownMul, op: 'mul', value: -0.2 },
      { stat: Stat.SecondaryDamageMul, op: 'mul', value: 0.15 },
    ],
    text: '−20 % de recharge des secondaires, +15 % de dégâts des secondaires',
  },
];

export default mods;
